/**
 * useAuditLogs Hook
 *
 * React Query hooks for audit log management.
 * @see US-19a.28
 */

import { useQuery } from '@tanstack/react-query';
import {
  auditLogsService,
  type AuditLogFilters,
} from '../services/audit-logs.service';

/**
 * Query key factory for audit logs
 */
export const auditLogKeys = {
  all: ['audit-logs'] as const,
  lists: () => [...auditLogKeys.all, 'list'] as const,
  list: (params: AuditLogFilters) => [...auditLogKeys.lists(), params] as const,
  details: () => [...auditLogKeys.all, 'detail'] as const,
  detail: (id: string) => [...auditLogKeys.details(), id] as const,
};

/**
 * Hook to fetch audit logs list with pagination
 */
export function useAuditLogs(params: AuditLogFilters) {
  return useQuery({
    queryKey: auditLogKeys.list(params),
    queryFn: () => auditLogsService.list(params),
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch a single audit log detail
 */
export function useAuditLogDetail(id: string | null) {
  return useQuery({
    queryKey: auditLogKeys.detail(id || ''),
    queryFn: () => auditLogsService.getById(id!),
    enabled: !!id,
  });
}

